import axios from 'axios'
import qs from 'qs'
import { changeLanToId } from '@/utils/tool.js'
import { getLocale } from '@/utils/lang.js'
import * as Sentry from '@sentry/vue'
import { captureException, captureMessage } from '@/utils/SentryUtils'
import { ApolloClient, InMemoryCache, ApolloLink, createHttpLink } from '@apollo/client'
import { type ApolloQueryResult } from '@apollo/client'
import { type DocumentNode } from 'graphql'
import { pageCurrency } from '@/utils/currencyState'
import { ApolloError } from '@apollo/client/errors'
const api_url = import.meta.env.VITE_API_URL
const language_id = changeLanToId(getLocale())

const RETRY_MAX_COUNT = 3
const RETRY_DELAY_BASE = 5000 // 5 second
const RETRY_JITTER = 3000 // 3 second
const RETRY_DELAY_MAX = 10000 // 10 second
const RETRY_DELAY_EXPONENTIAL = 2 // Exponential backoff factor

// core-service reads this on every GraphQL request to render prices in the customer's
// selected display currency (defaults to USD server-side if absent/unrecognized).
export function buildCurrencyHeaderLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const currency = pageCurrency()
    const { headers } = operation.getContext()
    operation.setContext({
      headers: {
        ...headers,
        'X-CurrencyType': currency
      }
    })
    return forward(operation)
  })
}

export default abstract class ServiceBase {
  //TODO 全局错误捕获

  private static handling401 = false
  protected static graphqlClient: ApolloClient<any>
  
  // Health check monitoring
  private static healthCheckInterval: NodeJS.Timeout | null = null
  static {
    const httpLink = createHttpLink({
      uri: '/api/graphql'
    })
    const authLink = new ApolloLink((operation, forward) => {
      //const token = localStorage.getItem('access_token')
      // operation.setContext({
      //   headers: {
      //     Authorization: token ? `Bearer ${token}` : ''
      //   }
      // })
      return forward(operation)
    })
    const currencyLink = buildCurrencyHeaderLink()
    ServiceBase.graphqlClient = new ApolloClient({
      link: authLink.concat(currencyLink).concat(httpLink),
      cache: new InMemoryCache()
    })
    setInterval(
      () => {
        ServiceBase.graphqlClient.cache.reset()
      },
      1000 * 60 * 3
    )
    
    // Start health monitoring
    ServiceBase.startHealthMonitoring()
  }

  private static startHealthMonitoring() {
    if (ServiceBase.healthCheckInterval) {
      clearInterval(ServiceBase.healthCheckInterval)
    }
    
    ServiceBase.healthCheckInterval = setInterval(() => {
      if (ServiceBase.handling401) {
        console.warn('[HealthCheck] Long running auth refresh detected', {
          handling401: ServiceBase.handling401,
          duration: 'over 30 seconds'
        })
        captureMessage('Long running auth refresh detected', {
          level: 'warning',
          extra: {
            handling401: ServiceBase.handling401,
            timestamp: new Date().toISOString()
          }
        })
      }
    }, 30000) // Check every 30 seconds
  }

  private logError(error: any, context: string) {
    console.error(`[${context}] Error:`, error)
    captureException(error, {
      tags: { context },
      extra: { 
        handling401: ServiceBase.handling401,
        timestamp: new Date().toISOString()
      }
    })
  }

  private sanitizeSentryRequest(request: any): any {
    if (request == null) return request

    // Used to match object keys for redaction (no query-string anchors needed).
    const sensitiveKeyPattern =
      /(cc_number(?:%5B\d+%5D)?|cc_cvc(?:%5B\d+%5D)?|cc_owner(?:%5B\d+%5D)?|cc_expire_date_month(?:%5B\d+%5D)?|cc_expire_date_year(?:%5B\d+%5D)?|password|pwd|token|signcode|verification_code|card_number|cvv)/i

    const truncate = (text: string, max = 3000) =>
      text.length > max ? text.slice(0, max) + `…[truncated:${text.length - max}]` : text

    if (typeof request === 'string') {
      const sanitized = request
        .replace(
          /((?:^|[&?])(cc_number(?:%5B\d+%5D)?|cc_cvc(?:%5B\d+%5D)?|cc_owner(?:%5B\d+%5D)?|cc_expire_date_month(?:%5B\d+%5D)?|cc_expire_date_year(?:%5B\d+%5D)?|password|pwd|token|signcode|verification_code|card_number|cvv)=)([^&]*)/gi,
          '$1[Filtered]'
        )
        .replace(
          /(flight%5Binfo%5D%5Badditional_info%5D=)([^&]*)/gi,
          '$1[Filtered]'
        )
      return truncate(sanitized)
    }

    if (typeof request === 'object') {
      try {
        const seen = new WeakSet()
        const sanitized = JSON.parse(
          JSON.stringify(request, (key, value) => {
            if (value && typeof value === 'object') {
              if (seen.has(value)) return '[Circular]'
              seen.add(value)
            }
            if (sensitiveKeyPattern.test(key)) return '[Filtered]'
            if (typeof value === 'string' && value.length > 1000) return `${value.slice(0, 1000)}…[truncated]`
            return value
          })
        )
        return sanitized
      } catch {
        return '[Unserializable request payload]'
      }
    }

    return request
  }

  private captureNonJsonResponse(url: any, request: any, response: any) {
    const data = response?.data
    const preview =
      typeof data === 'string' ? (data.length > 2000 ? data.slice(0, 2000) + '…' : data) : data

    captureMessage('non json response detected', {
      extra: {
        api: url,
        request: this.sanitizeSentryRequest(request),
        status: response?.status,
        contentType: response?.headers?.['content-type'],
        response: preview
      }
    })
  }

  private normalizeResponseData(url: any, request: any, response: any) {
    const data = response?.data

    if (data == null) return data

    // Some APIs return empty text for "ok" (e.g. markRead), which is not JSON but also not an error.
    if (response?.status === 204) return data

    if (typeof data === 'string') {
      const trimmed = data.trim()
      if (trimmed === '') return data

      // If backend returns JSON as text/plain, attempt to parse for compatibility.
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return JSON.parse(trimmed)
        } catch (e) {
          // fall through to capture below
        }
      }

      this.captureNonJsonResponse(url, request, response)
      return data
    }

    if (typeof data !== 'object') {
      this.captureNonJsonResponse(url, request, response)
    }

    return data
  }

  protected shouldIgnoreSentryForApi(url: any, error: any): boolean {
    const api = String(url ?? '')
    const status = error?.response?.status
    const isTransientNetworkError =
      !error?.response && (error?.message === 'Network Error' || error?.code === 'ECONNABORTED')

    if (api.includes('account/login/checkState')) {
      if (status === 403) {
        return true
      }
      if (isTransientNetworkError) {
        return true
      }
    }

    // These are non-critical "bootstrap" calls; on mobile (or with blockers) they can fail with status 0.
    // Ignore only transient network errors (no response), keep real HTTP errors visible.
    if (isTransientNetworkError) {
      const ignorable = [
        'module/pavmegamenu/getMenu',
        'common/header/getHeaderData',
        'common/common/getSettingByCode',
        'common/helpdesk/getLateNotice',
        'account/wishlist',
        // Product-detail page fires this as a fire-and-forget viewed marker.
        // Blockers / nav races can abort it with status 0 and it's not user-actionable.
        'module/recently_viewed/setProductViewed',
        // best-effort telemetry endpoint; drop transient offline/no-response noise
        'account/account/pagelog'
      ]
      if (ignorable.some((p) => api.includes(p))) {
        return true
      }
    }

    // checkout/payment 403 is a handled business/auth state in UI flow; avoid noisy Sentry events.
    if (api.includes('checkout/payment') && error?.response?.status === 403) {
      return true
    }

    // Ads are best-effort marketing content. Missing fills and backend instability should not page Sentry.
    if (api.includes('api/ads/fetch')) {
      if (isTransientNetworkError || status === 404 || status >= 500) {
        return true
      }
    }

    // Refund-rule metadata is supplementary copy/policy content with client fallbacks in flight flows.
    if (api.includes('product/product/trip_refund_rule')) {
      if (isTransientNetworkError || status === 404 || status >= 500) {
        return true
      }
    }

    return false
  }

  async retryWithBackoff(fn: Function, maxRetries = 3, context = 'RetryOperation'): Promise<any> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn()
      } catch (error) {
        if (i === maxRetries - 1) {
          this.logError(error, `${context}FinalAttemptFailed`)
          throw error
        }
        
        const delay = Math.min(1000 * Math.pow(2, i), 10000) // Exponential backoff with max 10s
        const jitter = Math.random() * 1000 // Add jitter to prevent thundering herd
        const totalDelay = delay + jitter
        
        this.logError({ 
          message: `Attempt ${i + 1} failed, retrying in ${Math.round(totalDelay)}ms`,
          attempt: i + 1,
          maxRetries,
          delay: totalDelay
        }, `${context}Retry`)
        
        await new Promise(resolve => setTimeout(resolve, totalDelay))
      }
    }
  }

  async handle401(): Promise<void> {
    if (!ServiceBase.handling401) {
      ServiceBase.handling401 = true
      try {
        await axios.post('/auth/refresh', {}, { 
          timeout: 10000,
          withCredentials: true 
        })
      } catch (error) {
        const status = (error as any)?.response?.status
        // 401/403 on refresh is a common "session expired" state, not a crash-worthy client error.
        if (status === 401 || status === 403) {
          // captureMessage('Auth refresh denied', {
          //   level: 'warning',
          //   tags: { context: 'AuthRefreshFailed' },
          //   extra: {
          //     status,
          //     handling401: ServiceBase.handling401,
          //     timestamp: new Date().toISOString()
          //   }
          // })
          window.dispatchEvent(new CustomEvent('auth:expired'))
        } else {
          this.logError(error, 'AuthRefreshFailed')
        }
        // Don't throw here, let the original request handle the error
        console.error('Auth refresh failed:', error)
      } finally {
        ServiceBase.handling401 = false
      }
    } else {
      //wait until completed with improved timeout and error handling
      return new Promise((resolve, reject) => {
        const MAX_WAIT_TIME = 5000 // Increased from 2000 to 5000ms
        const POLL_INTERVAL = 500
        let count = 0
        
        const timerId = setInterval(() => {
          count++
          if (!ServiceBase.handling401) {
            clearInterval(timerId)
            resolve()
          } else {
            if (count * POLL_INTERVAL >= MAX_WAIT_TIME) {
              clearInterval(timerId)
              const timeoutError = new Error('Authentication refresh timeout. Please refresh the page.')
              this.logError(timeoutError, 'AuthWaitTimeout')
              reject(timeoutError)
            }
          }
        }, POLL_INTERVAL)
      })
    }
  }

  async safeCall(callee?: () => Promise<any>, ...variables: any[]): Promise<any> {
    if (null == callee) {
      return null
    }
    if (ServiceBase.handling401) {
      await this.handle401()
    }
    try {
      return await callee()
    } catch (error) {
      if (error instanceof ApolloError) {
        const networkError = error.networkError as any
        let is401Error = false
        if (
          error.graphQLErrors != null &&
          error.graphQLErrors.length > 0 &&
          error.graphQLErrors[0]?.extensions?.statusCode == 401
        ) {
          is401Error = true
        }
        if (networkError != null && networkError.statusCode == 401) {
          is401Error = true
        }

        if (is401Error) {
          await this.handle401()
          //retry
          return await callee()
        } else {
          throw error
        }
      } else {
        throw error
      }
    }
  }

  async gqlQuery(
    query: DocumentNode,
    variables?: any,
    fetchPolicy: any = 'network-only',
    context?: Record<string, any>
  ): Promise<any> {
    return this.safeCall(async () => {
      const result = await ServiceBase.graphqlClient.query({
        query: query,
        variables: variables,
        fetchPolicy,
        context
      })
      if (result.errors != null) {
        throw result.errors
      }
      return result.data
    }, query)
  }

  async gqlMutate(mutation: DocumentNode, variables?: any, context?: Record<string, any>): Promise<any> {
    return this.safeCall(async () => {
      const result = await ServiceBase.graphqlClient.mutate({
        mutation,
        variables,
        fetchPolicy: 'no-cache',
        context
      })
      if (result.errors != null) {
        throw result.errors
      }
      return result.data
    }, mutation)
  }

  protected getApi(
    url: any,
    data?: any,
    config?: any,
    headers: any = { headers: { VUEDATATYPE: 'json' } },
    retryDelayBase = RETRY_DELAY_BASE,
    retryMaxCount = 0
  ): Promise<any> {
    return this.safeCall(() => {
      let retryCount = 0
      return new Promise((resolve, reject) => {
        const attempt = async () => {
            retryCount++
            try {
              // console.log(`Attempt ${retryCount} for URL: ${url}, retryMaxCount:${retryMaxCount}`);
              const response = await axios.create(headers).get(api_url + url, {
                ...config,
                params: data
              })
              // console.log('success: response:', response)
              resolve(this.normalizeResponseData(url, data, response))
            } catch (error) {
              if (this.ifShouldRetry(error, retryCount, retryMaxCount)) {
                // console.log('retryCount', retryCount)
                // console.log('retryError', error)
              await this.delay(this.getRetryDelay(retryCount, retryDelayBase))
              await attempt()
            } else {
              if (!this.shouldIgnoreSentryForApi(url, error)) {
                captureException(error, {
                  extra: {
                    api: url,
                    request: this.sanitizeSentryRequest(data)
                  }
                })
              }
              //console.log('error:', error)
              reject(error)
            }
          }
        }
        return attempt() // Start the first attempt
      })
    })
  }

  protected postApi(
    url: any,
    data: any,
    config?: any,
    headers: any = { headers: { VUEDATATYPE: 'json' } },
    retryDelayBase = RETRY_DELAY_BASE,
    retryMaxCount = 0
  ): Promise<any> {
    return this.safeCall(() => {
      let retryCount = 0
      const mergedConfig = {
        ...config,
        withCredentials: true
      }
      return new Promise((resolve, reject) => {
        const attempt = async () => {
          retryCount++
          try {
            // console.log(`Attempt ${retryCount} for URL: ${url}, retryMaxCount:${retryMaxCount}`);
            const response = await axios.create(headers).post(api_url + url, data, mergedConfig)
            resolve(this.normalizeResponseData(url, data, response))
          } catch (error) {
            if (this.ifShouldRetry(error, retryCount, retryMaxCount)) {
              await this.delay(this.getRetryDelay(retryCount, retryDelayBase))
              await attempt()
            } else {
              if (!this.shouldIgnoreSentryForApi(url, error)) {
                captureException(error, {
                  extra: {
                    api: url,
                    request: this.sanitizeSentryRequest(data)
                  }
                })
              }
              reject(error)
            }
          }
        }
        return attempt() // Start the first attempt
      })
    })
  }

  protected getRetryDelay = (retryCount: number, retryDelayBase: number): number => {
    const exponentialDelay = Math.min(
      retryDelayBase * Math.pow(RETRY_DELAY_EXPONENTIAL, retryCount - 1),
      RETRY_DELAY_MAX
    )
    const jitter = Math.random() * RETRY_JITTER
    return exponentialDelay + jitter
  }

  protected ifShouldRetry = (error: any, retryCount: number, retryMaxCount: number): boolean => {
    return (
      retryCount < retryMaxCount &&
      (error.code === 'ECONNABORTED' || // Request timeout
        !error.response || // No response (disconnected)
        (error.response && error.response.status >= 500))
    ) // 5xx server error;
  }

  protected delay = (delayTime: number) => new Promise((resolve) => setTimeout(resolve, delayTime))

  public webPost(
    params,
    callBack,
    url,
    errCallBack = undefined,
    retryDelayBase = RETRY_DELAY_BASE,
    retryMaxCount = 0
  ) {
    // Backward/loose compatibility:
    // Some callers pass (params, cb, url, retryDelayBase, retryMaxCount) without errCallBack.
    // In that case errCallBack is a number (e.g. 3000) and should be treated as retryDelayBase.
    if (typeof errCallBack === 'number') {
      retryMaxCount = typeof retryDelayBase === 'number' ? retryDelayBase : retryMaxCount
      retryDelayBase = errCallBack
      errCallBack = undefined
    }
    //console.log(api_url + url, params)
    if (!url.includes('&language_id=')) {
      url += '&language_id=' + language_id + '' // 这里替换为你的默认值 here replace with your default value
    }
    const data = qs.stringify(params)

    return this.postApi(url, data, undefined, undefined, retryDelayBase, retryMaxCount)
      .then((responseData) => {
        if (typeof responseData === 'string') {
          responseData = { url: api_url + url, data: responseData }
        } else {
          responseData.url = api_url + url
        }
        callBack(responseData)
      })
      .catch((error) => {
        if (errCallBack && typeof errCallBack === 'function') {
          errCallBack(error)
        }
      })
  }

  public webGet(
    params,
    callBack,
    url,
    errCallBack,
    retryDelayBase = RETRY_DELAY_BASE,
    retryMaxCount = 0
  ) {
    // Backward/loose compatibility: allow (params, cb, url, retryDelayBase, retryMaxCount)
    if (typeof errCallBack === 'number') {
      retryMaxCount = typeof retryDelayBase === 'number' ? retryDelayBase : retryMaxCount
      retryDelayBase = errCallBack
      errCallBack = undefined
    }
    const headers = { headers: { VUEDATATYPE: 'json', 'Content-Type': 'application/octet-stream' } }
    if (!url.includes('&language_id=')) {
      url += '&language_id=' + language_id + '' // 这里替换为你的默认值 here replace with your default value
    }
    return this.getApi(url, params, undefined, headers, retryDelayBase, retryMaxCount)
      .then((responseData) => {
        if (typeof responseData === 'string') {
          responseData = { url: api_url + url, data: responseData }
        } else {
          responseData.url = api_url + url
        }
        // console.log('responseData:', responseData)
        callBack(responseData)
      })
      .catch((error) => {
        if (errCallBack && typeof errCallBack === 'function') {
          errCallBack(error)
        }
      })
  }
}
